import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
// Models
import IReservationRequest from 'models/ReservationRequest';
// Types
import ReservationRequestStatus from 'types/ReservationRequestStatus';
// Selectors
import { selectReservationRequestById } from 'store/reservationRequests/reservationRequestsSelectors';
// MUI
import { makeStyles } from '@mui/styles';
import { Box, Card, Chip, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
// Utilities
import { formatStartEndDate } from 'utilities/DateTimeFormatter';
import dayjs from 'dayjs';

type Props = {
  reservationRequestId: number,
};

const ReservationRequestCard:React.FC<Props> = ({ reservationRequestId }) => {
  const classes = useStyles();

  const reservationRequest:IReservationRequest = useSelector((state:RootState) => selectReservationRequestById(state, { reservationRequestId }));

  return (
    <Card sx={{ p: 2, borderRadius: 0, borderBottom: '1px solid #eee', width: '100%' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} alignSelf="center">
          {reservationRequest.property ? (
            <Typography className={classes.title}>
              {reservationRequest.property.name}
            </Typography>
          ) : (
            <Typography className={classes.title} sx={{ color: '#d32f2f' }}>
              Property not set
            </Typography>
          )}
          <Typography className={classes.subtitle}>
            Created: {dayjs(reservationRequest.createdOn).format('MMMM DD YYYY')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3} alignSelf="center">
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography className={classes.subtitle} sx={{ display: {xs: 'block', sm: 'none'} }}>Date range</Typography>
            <Typography className={classes.text}>
              {formatStartEndDate(reservationRequest.start, reservationRequest.end)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: {xs: 2, sm: 0} }}>
            <Typography className={classes.subtitle} sx={{ display: {xs: 'block', sm: 'none'} }}>Status</Typography>
            <Box>
              {reservationRequest.status === ReservationRequestStatus.Confirmed && <Chip color={'success'} size="small" label="Confirmed" icon={<DoneAllIcon />} />}
              {reservationRequest.status === ReservationRequestStatus.Submitted && <Chip color={'info'} size="small" label="Submitted" icon={<DoneIcon />} />}
              {reservationRequest.status === ReservationRequestStatus.Rejected && <Chip color={'error'} size="small" label="Rejected" icon={<CloseIcon />} />}
              {reservationRequest.status === ReservationRequestStatus.Processing && <Chip color={'warning'} size="small" label="Processing" icon={<AccessTimeIcon />} />}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} alignSelf="center">
          <Typography className={classes.subtitle}>
            Notes
          </Typography>
          <Typography className={classes.text}>
            {reservationRequest.guestNotes || '-'}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );
}

export default ReservationRequestCard;

const useStyles = makeStyles({
  title: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '-0.35px',
  },
  subtitle: {
    fontSize: '12px',
    lineHeight: '166%',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  text: {
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
});
